import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PageHero, Reveal, SectionEyebrow } from "@/components/Primitives";
import { fetchSectors } from "@/lib/api";
import * as Icons from "lucide-react";
import SEO from "@/lib/seo";

export default function Sectors() {
  const [sectors, setSectors] = useState([]);
  useEffect(() => { fetchSectors().then(setSectors); }, []);

  return (
    <div data-testid="sectors-page">
      <SEO title="Sectors · The Cleanability Institute" description="Sector-specific cleanability guidance for healthcare, education, hospitality, airports, manufacturing and more." path="/sectors" />
      <PageHero
        testId="sectors-hero"
        eyebrow="Industry sectors"
        title="Every environment has its own cleanability physics."
        lead="Cleanability problems do not generalise. Hospitals are not airports, factories are not classrooms. The Institute publishes sector-specific guidance for the realities of each domain."
      />

      <section className="container-x section-y border-t border-border">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-border rounded-2xl overflow-hidden border border-border">
          {sectors.map((s, i) => {
            const Icon = Icons[s.icon] || Icons.Building2;
            return (
              <Reveal key={s.id} delay={i * 0.03}>
                <Link to={`/sectors/${s.id}`} data-testid={`sector-${s.id}`} className="bg-card p-8 md:p-10 hover-lift h-full group block">
                  <div className="flex items-center justify-between">
                    <Icon className="h-5 w-5 text-[hsl(var(--accent))]" />
                    <span className="font-mono-mini text-muted-foreground">{String(i + 1).padStart(2, "0")}</span>
                  </div>
                  <div className="mt-6 font-display text-xl md:text-2xl font-medium tracking-tight">{s.name}</div>
                  <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{s.summary}</p>
                  <div className="mt-8 text-xs font-medium underline-link">View sector dossier</div>
                </Link>
              </Reveal>
            );
          })}
        </div>
      </section>

      <section className="bg-secondary/30 border-t border-border">
        <div className="container-x section-y">
          <SectionEyebrow>Common ground</SectionEyebrow>
          <h2 className="mt-6 font-display text-3xl md:text-5xl tracking-tight font-medium max-w-3xl text-balance">
            Different domains, shared failure modes.
          </h2>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              ["Inaccessible geometries", "Spaces that cannot be reached without acrobatics."],
              ["Wrong materials", "Surfaces that fail under their own cleaning regime."],
              ["Phantom labour", "Operational burden invisible to procurement and design."],
              ["Disposable workers", "Roles designed for churn rather than mastery."],
            ].map(([t, b], i) => (
              <Reveal key={t} delay={i * 0.05}>
                <div data-testid={`failure-${i}`} className="rounded-2xl border border-border bg-card p-6 hover-lift h-full">
                  <div className="font-mono-mini text-[hsl(var(--accent))]">Pattern {String(i + 1).padStart(2, "0")}</div>
                  <div className="mt-3 font-display text-lg font-medium tracking-tight">{t}</div>
                  <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{b}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
